const toText = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const PREVIEW_TERMINAL_STATUSES = new Set(["ready", "processing", "failed"]);
const RASTER_ARTWORK_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;
const DERIVED_PREVIEW_EXT_RX = /\.(ai|eps|ps|pdf)(?:$|[?#])/i;
const DERIVED_PREVIEW_MIME_TYPES = new Set([
  "application/pdf",
  "application/postscript",
  "application/eps",
  "application/x-eps",
  "image/x-eps",
  "application/illustrator",
  "application/ai",
]);

const toCustomerFileId = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizePreviewStatusValue = (value) => toText(value).toLowerCase();

export const normalizeArtworkOriginalUrl = (value = "") => toText(value);

export const getArtworkOriginalUrl = (entry = {}) =>
  normalizeArtworkOriginalUrl(
    typeof entry === "string"
      ? entry
      :
    entry?.originalUrl ??
      entry?.originalFileUrl ??
      entry?.filePath ??
      entry?.Image ??
      entry?.selectedImage ??
      entry?.Location ??
      entry?.url ??
      entry?.path
  );

export const getArtworkPreviewUrl = (entry = {}) =>
  toText(
    typeof entry === "string"
      ? ""
      :
    entry?.previewImageUrl ??
      entry?.previewUrl ??
      entry?.PreviewImageUrl ??
      entry?.PreviewUrl ??
      entry?.thumbnailUrl ??
      entry?.ThumbnailUrl
  );

export const getArtworkCustomerFileId = (entry = {}) =>
  toCustomerFileId(
    typeof entry === "string"
      ? null
      : entry?.customerFileId ?? entry?.CustomerFileId
  );

export const getCustomerFileRecordId = (entry = {}) =>
  toCustomerFileId(
    typeof entry === "string"
      ? null
      : entry?.id
  );

export const getArtworkPreviewStatus = (entry = {}) =>
  normalizePreviewStatusValue(
    typeof entry === "string"
      ? ""
      : entry?.previewStatus ?? entry?.PreviewStatus ?? entry?.status
  );

export const isArtworkOriginalRasterImage = (entry = {}) => {
  const normalized = normalizeArtworkFileEntry(entry);
  const candidate = normalized.originalUrl || normalized.fileName || "";
  return RASTER_ARTWORK_EXT_RX.test(candidate);
};

export const isArtworkDerivedPreviewCandidate = (entry = {}) => {
  const normalized = normalizeArtworkFileEntry(entry);
  if (!normalized.originalUrl && !normalized.fileName) return false;
  if (isArtworkOriginalRasterImage(normalized)) return false;

  const candidate = normalized.originalUrl || normalized.fileName || "";
  const mimeType = toText(normalized.mimeType).toLowerCase();

  return (
    DERIVED_PREVIEW_EXT_RX.test(candidate) ||
    DERIVED_PREVIEW_EXT_RX.test(normalized.fileName || "") ||
    DERIVED_PREVIEW_MIME_TYPES.has(mimeType)
  );
};

export const normalizeArtworkFileEntry = (entry) => {
  if (!entry) {
    return {
      id: null,
      filePath: "",
      originalUrl: "",
      originalFileUrl: "",
      previewImageUrl: "",
      previewUrl: "",
      fileName: "",
      mimeType: "",
      previewStatus: "",
      previewError: "",
      previewGeneratedAt: "",
      folder: "",
      createdAt: "",
      customerFileId: null,
    };
  }

  if (typeof entry === "string") {
    return {
      id: null,
      filePath: entry,
      originalUrl: entry,
      originalFileUrl: entry,
      previewImageUrl: "",
      previewUrl: "",
      fileName: "",
      mimeType: "",
      previewStatus: "",
      previewError: "",
      previewGeneratedAt: "",
      folder: "",
      createdAt: "",
      customerFileId: null,
    };
  }

  const originalUrl = getArtworkOriginalUrl(entry);
  const previewImageUrl = getArtworkPreviewUrl(entry);
  const customerFileId = getArtworkCustomerFileId(entry);

  return {
    ...entry,
    id: entry?.id ?? null,
    filePath: originalUrl,
    originalUrl,
    originalFileUrl: originalUrl,
    previewImageUrl,
    previewUrl: previewImageUrl,
    fileName: toText(entry?.fileName ?? entry?.name),
    mimeType: toText(entry?.mimeType ?? entry?.type),
    previewStatus: toText(
      entry?.previewStatus ?? entry?.PreviewStatus ?? entry?.status
    ),
    previewError: toText(entry?.previewError ?? entry?.PreviewError),
    previewGeneratedAt: toText(
      entry?.previewGeneratedAt ?? entry?.PreviewGeneratedAt
    ),
    folder: toText(entry?.folder ?? entry?.folderName),
    createdAt: toText(entry?.createdAt ?? entry?.uploadedAt ?? entry?.updatedAt),
    customerFileId,
  };
};

export const normalizeCustomerFileEntry = (entry) => {
  const normalized = normalizeArtworkFileEntry(entry);
  return {
    ...normalized,
    customerFileId:
      getArtworkCustomerFileId(entry) ??
      getCustomerFileRecordId(entry),
  };
};

export const shouldEnsureArtworkPreview = (entry) => {
  const normalized = normalizeArtworkFileEntry(entry);
  if (!normalized.customerFileId) return false;
  if (!normalized.originalUrl) return false;
  if (normalized.previewImageUrl) return false;
  if (PREVIEW_TERMINAL_STATUSES.has(getArtworkPreviewStatus(normalized))) return false;
  if (isArtworkOriginalRasterImage(normalized)) return false;
  if (!isArtworkDerivedPreviewCandidate(normalized)) return false;
  return true;
};

export const collectEnsurePreviewFileIds = (entries = []) => {
  const deduped = new Set();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const normalized = normalizeArtworkFileEntry(entry);
    if (!shouldEnsureArtworkPreview(normalized)) return;
    deduped.add(normalized.customerFileId);
  });
  return Array.from(deduped);
};

export const buildArtworkSelectionPayload = (entry) => {
  const normalized = normalizeArtworkFileEntry(entry);
  return {
    originalUrl: normalized.originalUrl || normalized.filePath || "",
    previewUrl: normalized.previewImageUrl || null,
    customerFileId: normalized.customerFileId,
    fileName: normalized.fileName || "",
    mimeType: normalized.mimeType || "",
    previewStatus: normalized.previewStatus || "",
    previewError: normalized.previewError || "",
    previewGeneratedAt: normalized.previewGeneratedAt || "",
    folder: normalized.folder || "",
    createdAt: normalized.createdAt || "",
    filePath: normalized.filePath || "",
  };
};

export const resolveArtworkCustomerFile = ({
  artwork,
  customerFilesById = new Map(),
  customerFilesByOriginalUrl = new Map(),
} = {}) => {
  const normalizedArtwork = normalizeArtworkFileEntry(artwork);
  const explicitCustomerFileId = getArtworkCustomerFileId(artwork);
  const originalUrl = normalizeArtworkOriginalUrl(normalizedArtwork.originalUrl);

  const matchedCustomerFile =
    (explicitCustomerFileId != null
      ? customerFilesById.get(String(explicitCustomerFileId))
      : null) ||
    (originalUrl ? customerFilesByOriginalUrl.get(originalUrl) : null) ||
    null;

  if (!matchedCustomerFile) {
    return {
      ...normalizedArtwork,
      customerFileId: explicitCustomerFileId ?? null,
    };
  }

  return {
    ...normalizedArtwork,
    customerFileId:
      matchedCustomerFile.customerFileId ??
      explicitCustomerFileId ??
      null,
    previewImageUrl:
      normalizedArtwork.previewImageUrl ||
      matchedCustomerFile.previewImageUrl ||
      "",
    previewUrl:
      normalizedArtwork.previewUrl ||
      matchedCustomerFile.previewImageUrl ||
      matchedCustomerFile.previewUrl ||
      "",
    previewStatus:
      matchedCustomerFile.previewStatus ||
      normalizedArtwork.previewStatus ||
      "",
    previewError:
      matchedCustomerFile.previewError ??
      normalizedArtwork.previewError ??
      "",
    previewGeneratedAt:
      matchedCustomerFile.previewGeneratedAt ||
      normalizedArtwork.previewGeneratedAt ||
      "",
  };
};
